import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FaBolt, FaCheckCircle, FaSync, FaTimesCircle } from 'react-icons/fa';
import { catalogNodeService } from '../../../../services/apiService';

function sellerLabel(approval) {
  const s = approval.sellerId;
  if (!s || typeof s === 'string') return '—';
  return s.vendorDetails?.businessName || s.name || s.email || '—';
}

function vehiclePath(approval) {
  return [
    approval.make,
    approval.model,
    approval.generation,
    approval.variant !== 'ALL' ? approval.variant : null,
    approval.fuelType !== 'ALL' ? approval.fuelType : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

export default function CatalogApprovalsPanel() {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await catalogNodeService.listApprovals();
      setApprovals(res?.data || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load catalog approvals');
      setApprovals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = async (approvalId) => {
    try {
      setActingId(approvalId);
      await catalogNodeService.approveApproval(approvalId);
      toast.success('Profile E listing approved and published');
      await load();
    } catch (err) {
      toast.error(err.message || 'Could not approve listing');
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async (approvalId) => {
    const reason = window.prompt('Rejection reason (optional):') ?? '';
    if (reason === null) return;
    try {
      setActingId(approvalId);
      await catalogNodeService.rejectApproval(approvalId, reason);
      toast.success('Profile E listing rejected');
      await load();
    } catch (err) {
      toast.error(err.message || 'Could not reject listing');
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Profile E approvals</h2>
          <p className="text-sm text-slate-500 mt-1">
            Electrical parts (Profile E) require admin review before catalog listing goes live.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50"
        >
          <FaSync className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading approvals…</p>
      ) : approvals.length === 0 ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-6 py-10 text-center">
          <FaCheckCircle className="mx-auto text-3xl text-emerald-600 mb-2" />
          <p className="font-semibold text-emerald-900">No pending Profile E approvals</p>
          <p className="text-sm text-emerald-700 mt-1">All electrical catalog listings are reviewed.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Part</th>
                <th className="px-4 py-3">Seller</th>
                <th className="px-4 py-3">Vehicle path</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Submitted</th>
                <th className="px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {approvals.map((approval) => (
                <tr key={approval._id} className="hover:bg-slate-50/80">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900 flex items-center gap-2">
                      <FaBolt className="text-amber-500 text-xs" />
                      {approval.partCategory}
                    </div>
                    <div className="text-[10px] font-mono text-slate-400 truncate max-w-[200px]">
                      {approval.catalogNodeId}
                    </div>
                    <span className="text-xs font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded mt-1 inline-block">
                      Profile {approval.profile || 'E'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{sellerLabel(approval)}</td>
                  <td className="px-4 py-3 text-slate-700">{vehiclePath(approval)}</td>
                  <td className="px-4 py-3 text-slate-900 font-medium">
                    {approval.price != null ? `₹${Number(approval.price).toLocaleString('en-IN')}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">
                    {approval.createdAt
                      ? new Date(approval.createdAt).toLocaleString('en-IN', {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })
                      : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={actingId === approval._id}
                        onClick={() => handleApprove(approval._id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50"
                      >
                        <FaCheckCircle className="text-[10px]" />
                        {actingId === approval._id ? '…' : 'Approve'}
                      </button>
                      <button
                        type="button"
                        disabled={actingId === approval._id}
                        onClick={() => handleReject(approval._id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-200 text-red-700 text-xs font-semibold hover:bg-red-50 disabled:opacity-50"
                      >
                        <FaTimesCircle className="text-[10px]" />
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
